import { DateTime } from 'luxon';
import React, { useRef, useState } from 'react';
import { useMaskedInputProps } from 'react-native-mask-input';
import { DATE_TIME_DISPLAY_FORMAT, DATE_DISPLAY_FORMAT, isValidDateTimeValue, isValidDateValue } from './utils';
import InputBase, { InputBaseProps, innerTextInputStyles } from '../InputBase';
import { TextInput, TextInputProps, View } from 'react-native';
import { useIsDisabledByParent } from '../../IsDisabledProvider';
import { getIntl } from '../../../../utils/intl';
import CalendarPickerPrompt from './CalendarPickerPrompt';

const intl = getIntl('dateTimePicker', {
    dateHelperText: 'MM/DD/YYYY (12/31/2021)',
    dateTimeHelperText: 'MM/DD/YYYY HH:MM AM/PM (12/31/2021 01:32 PM)',
    dateFormatError: 'Invalid date format.  Date must be in the format MM/DD/YYYY',
    dateTimeFormatError: 'Invalid time format.  Time must be in the format MM/DD/YYYY HH:MM AM/PM',
});

type UseMaskedInputReturnValue = ReturnType<typeof useMaskedInputProps>;

/**
 * MM/DD/YYYY mask that forgives single-digit entry: typing "2" for February
 * auto-pads to "02" (a literal '0' is emitted and the typed digit fills the
 * second slot). Same for days 4-9. The stock Masks.DATE_MMDDYYYY rejects any
 * month not starting with 0/1, forcing users to know they must type "02".
 */
export const lenientDateMask = (text = '') => {
    const digits = text.replace(/\D/g, '');
    const monthMask = digits[0] && /[2-9]/.test(digits[0]) ? ['0', /\d/] : [/[0-1]/, /\d/];
    const dayIndex = 2;
    const dayMask = digits[dayIndex] && /[4-9]/.test(digits[dayIndex]) ? ['0', /\d/] : [/[0-3]/, /\d/];
    return [...monthMask, '/', ...dayMask, '/', /\d/, /\d/, /\d/, /\d/];
};

export type DateTimePickerOwnProps = {
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled?: boolean;
    includesTime?: boolean;
};

export type DateTimePickerProps = DateTimePickerOwnProps &
    Omit<InputBaseProps, 'renderInputField' | 'onInputPress' | 'onLayout'>;

/**
 * Gets the input props given the mask props and focus state
 *
 * We have to be smart here, we don't want to always pass down the placeholder props.  This
 * can result in the default placeholder showing as an inputted value and never transitioning
 * the input back to a "no value" state.
 */
const getMaskedInputProps = (maskProps: UseMaskedInputReturnValue, hasFocus: boolean): Partial<TextInputProps> => {
    if (hasFocus) return maskProps;

    if (maskProps.value && maskProps.value !== maskProps.placeholder) {
        return maskProps;
    }

    return {
        value: '',
    };
};

const isValidISODate = (value: string | null) => {
    if (!value) return false;

    return DateTime.fromISO(value).isValid;
};

const getDateValue = (value: string | null): string | null => {
    if (!value || !isValidISODate(value)) return null;

    return DateTime.fromISO(value).toFormat(DATE_DISPLAY_FORMAT);
};

const getDateTimeValue = (value: string | null): string | null => {
    if (!value || !isValidISODate(value)) return null;

    return DateTime.fromISO(value).toFormat(DATE_TIME_DISPLAY_FORMAT);
};

const toISO = (value: string | null, includeTime: boolean) => {
    if (!value) return null;

    if (!includeTime && isValidDateValue(value)) {
        const dateTime = DateTime.fromFormat(value, DATE_DISPLAY_FORMAT);
        return dateTime.toISODate();
    } else if (includeTime && isValidDateTimeValue(value)) {
        const dateTime = DateTime.fromFormat(value, DATE_TIME_DISPLAY_FORMAT);
        return dateTime.toISO();
    } else {
        return null;
    }
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({ isDisabled, value, onChange, includesTime, ...toPass }) => {
    const isMaybeDisabledByParent = useIsDisabledByParent(isDisabled);
    const dateInputRef = useRef<React.LegacyRef<TextInput> | null>(null);
    const [hasFocus, setHasFocus] = useState(false);
    const [hasHadFocus, setHasHadFocus] = React.useState(false);
    // internal state of the date text input value
    const [textValue, setTextValue] = useState<string | null>(() => {
        return includesTime ? getDateTimeValue(value) : getDateValue(value);
    });

    const dateMaskedInputProps: UseMaskedInputReturnValue = useMaskedInputProps({
        value: textValue || undefined,
        mask: lenientDateMask,
        onChangeText: (text) => {
            setTextValue(text);
        },
        maskAutoComplete: true,
    });
    const dateTimeMaskedInputProps: UseMaskedInputReturnValue = useMaskedInputProps({
        value: textValue || undefined,
        mask: (value) => [...lenientDateMask(value), ' ', /\d/, /\d/, ':', /\d/, /\d/, ' ', /[APap]/, 'M'],
        onChangeText: (text) => {
            setTextValue(text ? text.toLocaleUpperCase() : text); // capitalize a or p in AM/PM
        },
        maskAutoComplete: true,
        showObfuscatedValue: true,
    });

    // anytime the text value changes and is valid or cleared, update parent
    React.useEffect(() => {
        if (includesTime) {
            if (isValidDateTimeValue(textValue)) {
                const newValue = toISO(textValue, includesTime);
                onChange(newValue);
            } else if (!textValue && value) {
                onChange(null);
            }
        } else {
            if (isValidDateValue(textValue)) {
                const newValue = toISO(textValue, !!includesTime);
                onChange(newValue);
            } else if (!textValue && value) {
                onChange(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textValue]);

    const handleOnFocus = () => {
        setHasFocus(true);
        setHasHadFocus(true);
        toPass?.onFocus?.();
    };

    const handleOnBlur = () => {
        setHasFocus(false);
        (toPass?.onBlur as (() => void) | undefined)?.();
    };

    const hasFormatError = includesTime ? !isValidDateTimeValue(textValue) : !isValidDateValue(textValue);
    const getHasInternalFormatError = () => hasHadFocus && hasFormatError && !hasFocus && textValue;
    const getErrorMessage = () => {
        if (getHasInternalFormatError()) {
            return intl(includesTime ? 'dateTimeFormatError' : 'dateFormatError');
        } else {
            return toPass.error;
        }
    };

    return (
        <InputBase
            {...toPass}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            onInputPress={() => {
                (dateInputRef.current as any)?.focus?.();
            }}
            isDisabled={isMaybeDisabledByParent}
            error={getHasInternalFormatError() ? getErrorMessage() : toPass.error}
            value={textValue}
            maskPlaceholder={dateMaskedInputProps.placeholder}
            renderInputField={({ onFocus, onBlur, hasFocus }) => {
                const maskInputProps = includesTime ? dateTimeMaskedInputProps : dateMaskedInputProps;

                return (
                    <View style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                        <TextInput
                            ref={dateInputRef as any}
                            testID={toPass.testID}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            style={innerTextInputStyles}
                            // make sure we don't show the mask placeholder as a value if no focus and no altercations
                            {...getMaskedInputProps(maskInputProps, hasFocus)}
                            onChange={(e) => dateMaskedInputProps.onChangeText(e.nativeEvent.text)}
                        />
                        <CalendarPickerPrompt
                            type={includesTime ? 'date-time' : 'date'}
                            value={value}
                            testID={toPass.testID}
                            onChange={(value) => {
                                setTextValue(includesTime ? getDateTimeValue(value) : getDateValue(value));
                            }}
                        />
                    </View>
                );
            }}
        />
    );
};

export default React.memo(DateTimePicker);
